import { useState } from "react";
import { Dropdown } from "primereact/dropdown";
import { FormField } from "./FormField";
import { formStyles } from "./formStyles";
import { AdminCalendar } from "./AdminCalendar";

type RelativeDateOption =
  | "none"
  | "custom"
  | "1-month-before"
  | "1-hour-before"
  | "2-hours-before"
  | "6-hours-before"
  | "12-hours-before"
  | "24-hour-before"
  | "1-hour-after"
  | "2-hours-after"
  | "1-day-before"
  | "2-days-before"
  | "1-week-before"
  | "2-weeks-before";

interface RelativeDatePickerProps {
  id: string;
  label: string;
  optional?: boolean;
  helperText?: string;
  error?: string;
  value: Date | null;
  onChange: (value: Date | null) => void;
  referenceDate: Date | null;
  referenceDateLabel?: string;
  allowAfterOptions?: boolean;
}

const allRelativeDateOptions: Array<{ label: string; value: RelativeDateOption }> =
  [
    { label: "N/A", value: "none" },
    { label: "Custom", value: "custom" },
    { label: "1 month before", value: "1-month-before" },
    { label: "1 hour before", value: "1-hour-before" },
    { label: "2 hours before", value: "2-hours-before" },
    { label: "6 hours before", value: "6-hours-before" },
    { label: "12 hours before", value: "12-hours-before" },
    { label: "24 hours before", value: "24-hour-before" },
    { label: "1 hour after", value: "1-hour-after" },
    { label: "2 hours after", value: "2-hours-after" },
    { label: "1 day before", value: "1-day-before" },
    { label: "2 days before", value: "2-days-before" },
    { label: "1 week before", value: "1-week-before" },
    { label: "2 weeks before", value: "2-weeks-before" },
  ];

function getRelativeDateOptions(allowAfter: boolean): Array<{ label: string; value: RelativeDateOption }> {
  if (allowAfter) {
    return allRelativeDateOptions;
  }
  return allRelativeDateOptions.filter(
    (opt) => opt.value !== "1-hour-after" && opt.value !== "2-hours-after"
  );
}

function isRelativeDateOption(value: unknown, allowAfter: boolean): value is RelativeDateOption {
  const options = getRelativeDateOptions(allowAfter);
  return (
    typeof value === "string" &&
    options.some((opt) => opt.value === value)
  );
}

function calculateRelativeDate(
  option: RelativeDateOption,
  referenceDate: Date | null,
): Date | null {
  if (!referenceDate || option === "custom" || option === "none") {
    return null;
  }

  const result = new Date(referenceDate);

  switch (option) {
    case "1-month-before":
      result.setMonth(result.getMonth() - 1);
      break;
    case "1-hour-before":
      result.setHours(result.getHours() - 1);
      break;
    case "2-hours-before":
      result.setHours(result.getHours() - 2);
      break;
    case "6-hours-before":
      result.setHours(result.getHours() - 6);
      break;
    case "12-hours-before":
      result.setHours(result.getHours() - 12);
      break;
    case "24-hour-before":
      result.setHours(result.getHours() - 24);
      break;
    case "1-hour-after":
      result.setHours(result.getHours() + 1);
      break;
    case "2-hours-after":
      result.setHours(result.getHours() + 2);
      break;
    case "1-day-before":
      result.setDate(result.getDate() - 1);
      result.setHours(23, 59, 59, 999);
      break;
    case "2-days-before":
      result.setDate(result.getDate() - 2);
      result.setHours(23, 59, 59, 999);
      break;
    case "1-week-before":
      result.setDate(result.getDate() - 7);
      result.setHours(23, 59, 59, 999);
      break;
    case "2-weeks-before":
      result.setDate(result.getDate() - 14);
      result.setHours(23, 59, 59, 999);
      break;
    default:
      return null;
  }

  return result;
}

function getRelativeDateOption(
  value: Date | null,
  referenceDate: Date | null,
): RelativeDateOption {
  if (!value || !referenceDate) {
    return "none";
  }

  // For day/week options, compare calendar dates (ignoring time differences)
  // This handles cases where reference date is at different times of day
  const valueDate = new Date(
    value.getFullYear(),
    value.getMonth(),
    value.getDate(),
  );
  const referenceDateOnly = new Date(
    referenceDate.getFullYear(),
    referenceDate.getMonth(),
    referenceDate.getDate(),
  );
  const diffDateMs = referenceDateOnly.getTime() - valueDate.getTime();
  const diffCalendarDays = diffDateMs / (1000 * 60 * 60 * 24);

  // Check if the value time is at end of day (23:59:59.x)
  // Day/week options set time to 23:59:59.999, while hour options preserve the time
  const valueHours = value.getHours();
  const valueMinutes = value.getMinutes();
  const valueSeconds = value.getSeconds();
  const isEndOfDay =
    valueHours === 23 && valueMinutes === 59 && valueSeconds >= 59;

  // Check calendar day differences FIRST (before hour checks)
  // Only match if time is at end of day - this distinguishes "1 day before" from "24 hours before"
  // "1 day before" sets time to 23:59:59.999, while "24 hours before" preserves the original time
  if (diffCalendarDays === 1 && isEndOfDay) return "1-day-before";
  if (diffCalendarDays === 2 && isEndOfDay) return "2-days-before";
  if (diffCalendarDays === 7 && isEndOfDay) return "1-week-before";
  if (diffCalendarDays === 14 && isEndOfDay) return "2-weeks-before";

  // Only check hour-based differences AFTER calendar day checks
  const diffMs = referenceDate.getTime() - value.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);

  // Check with 1 minute tolerance for rounding (hours)
  const hourTolerance = 1 / 60;
  const timeToleranceMs = 60 * 1000;

  // Month-based option preserves clock time but shifts month.
  const oneMonthBefore = new Date(referenceDate);
  oneMonthBefore.setMonth(oneMonthBefore.getMonth() - 1);
  if (Math.abs(oneMonthBefore.getTime() - value.getTime()) <= timeToleranceMs) {
    return "1-month-before";
  }

  if (Math.abs(diffHours - 1) < hourTolerance) return "1-hour-before";
  if (Math.abs(diffHours - 2) < hourTolerance) return "2-hours-before";
  if (Math.abs(diffHours - 6) < hourTolerance) return "6-hours-before";
  if (Math.abs(diffHours - 12) < hourTolerance) return "12-hours-before";
  if (Math.abs(diffHours - 24) < hourTolerance) return "24-hour-before";
  if (Math.abs(diffHours + 1) < hourTolerance) return "1-hour-after";
  if (Math.abs(diffHours + 2) < hourTolerance) return "2-hours-after";

  return "custom";
}

export function RelativeDatePicker({
  id,
  label,
  optional,
  helperText,
  error,
  value,
  onChange,
  referenceDate,
  referenceDateLabel = "event start",
  allowAfterOptions = false,
}: RelativeDatePickerProps) {
  // Allows the user to force "Custom" even if the selected date happens to match a preset.
  const [customOverride, setCustomOverride] = useState(false);

  const derivedOption = getRelativeDateOption(value, referenceDate);
  // If the derived option is an "after" option but they're not allowed, treat as custom
  const isAfterOptionNotAllowed = 
    (derivedOption === "1-hour-after" || derivedOption === "2-hours-after") && !allowAfterOptions;
  const selectedOption: RelativeDateOption =
    customOverride || isAfterOptionNotAllowed ? "custom" : derivedOption;

  const handleOptionChange = (option: RelativeDateOption) => {
    if (option === "none") {
      setCustomOverride(false);
      // Clear the date
      onChange(null);
    } else if (option === "custom") {
      setCustomOverride(true);
      // Keep current value as-is when switching to custom
      // Don't change the value
    } else {
      setCustomOverride(false);
      const calculatedDate = calculateRelativeDate(option, referenceDate);
      onChange(calculatedDate);
    }
  };

  const handleCalendarChange = (date: Date | null) => {
    setCustomOverride(false);
    onChange(date);
  };

  const displayHelperText =
    helperText ||
    (referenceDateLabel
      ? `Dates are relative to ${referenceDateLabel}`
      : undefined);

  return (
    <FormField
      id={id}
      label={label}
      optional={optional}
      helperText={displayHelperText}
      error={error}
    >
      <div className={`flex flex-column gap-2 ${error ? "p-invalid" : ""}`}>
        <Dropdown
          value={selectedOption}
          options={getRelativeDateOptions(allowAfterOptions)}
          onChange={(e) => {
            if (isRelativeDateOption(e.value, allowAfterOptions)) {
              handleOptionChange(e.value);
            }
          }}
          className={`w-full ${error ? "p-invalid" : ""}`}
          placeholder="Select predefined option"
        />
        {selectedOption === "custom" && (
          <AdminCalendar
            id={id}
            value={value}
            onChange={(e) => handleCalendarChange(e.value as Date)}
            showTime
            timeOnly={false}
            className="w-full"
            inputStyle={formStyles.input}
          />
        )}
      </div>
    </FormField>
  );
}
